import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, CircleAlert, Flag, ListChecks, Loader2, RotateCcw, Timer } from "lucide-react";
import { ReviewScreen, useStrikes } from "@/components/test-section/ExamTools";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BuildingView, PassagePane, QuestionPane, Palette } from "@/components/test-section/SessionParts";
import { SKILL_LABELS } from "@/components/test-section/meta";

function useCountdown(session, onExpire) {
  const [left, setLeft] = useState(null);
  const fired = useRef(false);
  useEffect(() => {
    if (!session?.timer_minutes || session.status === "finished" || session.building) return undefined;
    const k = `pm-sci-timer-${session.id}`;
    const startedAt = +(localStorage.getItem(k) || Date.now());
    localStorage.setItem(k, String(startedAt));
    const tick = () => {
      const s = Math.max(0, session.timer_minutes * 60 - Math.floor((Date.now() - startedAt) / 1000));
      setLeft(s);
      if (s === 0 && !fired.current) { fired.current = true; onExpire(); }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [session?.id, session?.timer_minutes, session?.status, session?.building, onExpire]);
  return left;
}

function Breakdown({ title, data, label }) {
  const rows = Object.entries(data || {});
  if (!rows.length) return null;
  return (
    <div className="pm-card p-5">
      <div className="font-display font-bold mb-3">{title}</div>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between text-sm py-1" data-testid={`sp-breakdown-${k}`}>
          <span>{label ? label(k) : k}</span><span className="font-mono">{v.correct}/{v.total}</span>
        </div>
      ))}
    </div>
  );
}

function ReviewView({ session, onRetry, retrying }) {
  const r = session.results;
  let n = 0;
  return (
    <div className="max-w-6xl mx-auto" data-testid="sp-review">
      <Link to="/app/practice-test" className="text-xs inline-flex items-center gap-1 mb-3 hover:underline" style={{ color: "var(--primary)" }} data-testid="sp-back-hub"><ArrowLeft size={12} /> Test Section</Link>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--text-muted)" }}>{session.mode_name} · review</div>
          <div className="font-display text-6xl font-bold" style={{ color: "var(--primary)" }} data-testid="sp-score">{r.correct}/{r.total}</div>
          <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{Math.round(r.accuracy * 100)}% correct</div>
        </div>
        <button onClick={onRetry} disabled={retrying} className="pm-btn-secondary inline-flex items-center gap-2" data-testid="sp-retry-btn">
          {retrying ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Retry these passages (free)
        </button>
      </div>
      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <Breakdown title="By reasoning skill" data={r.by_skill} label={(k) => SKILL_LABELS[k] || k} />
        <Breakdown title="By content category" data={r.by_category} />
        <Breakdown title="By passage type" data={r.by_type} />
      </div>
      {session.passages.map((p) => (
        <div key={p.id} className="grid lg:grid-cols-2 gap-6 mb-12 pb-10 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="pm-card p-6 min-w-0"><PassagePane passage={p} /></div>
          <div className="space-y-8 min-w-0">
            {p.questions.map((q, qi) => {
              n += 1;
              return <QuestionPane key={qi} q={q} number={n} answer={session.answers[`${p.id}:${qi}`]} review onChoose={() => {}} />;
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function SciencePractice() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [cur, setCur] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [strikes, toggleStrike] = useStrikes(sessionId);
  const [locked, setLocked] = useState(() => new Set());

  const load = useCallback(() => api.get(`/test-section/sessions/${sessionId}`).then((r) => { setSession(r.data); return r.data; }), [sessionId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!session?.building) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [session?.building, load]);

  const items = useMemo(() => (session?.passages || []).flatMap((p) => p.questions.map((q, qi) => ({ key: `${p.id}:${qi}`, p, q }))), [session?.passages]);
  const passages = session?.passages || [];
  const safeCur = Math.min(cur, Math.max(0, items.length - 1));
  const curPassageIdx = Math.max(0, passages.findIndex((p) => p.id === items[safeCur]?.p?.id));
  const curPid = passages[curPassageIdx]?.id;
  const isLocked = curPid ? locked.has(curPid) : false;
  const passageStart = useMemo(() => passages.map((p) => items.findIndex((it) => it.p.id === p.id)), [passages, items]);

  const finish = useCallback(async () => {
    setFinishing(true);
    try {
      const r = await api.post(`/test-section/sessions/${sessionId}/finish`);
      localStorage.removeItem(`pm-sci-timer-${sessionId}`);
      setSession(r.data); window.scrollTo(0, 0);
    } finally { setFinishing(false); setConfirm(false); }
  }, [sessionId]);
  const left = useCountdown(session, finish);

  const save = (key, patch) => {
    const [pid] = String(key).split(":");
    if (locked.has(pid)) return;
    setSession((s) => ({ ...s, answers: { ...s.answers, [key]: { ...(s.answers[key] || {}), ...patch } } }));
    api.post(`/test-section/sessions/${sessionId}/answer`, { key, ...patch }).catch(() => {});
  };

  const goNextPassage = () => {
    if (!curPid) return;
    if (curPassageIdx >= passages.length - 1) { setConfirm(true); return; }
    setLocked((s) => { const n = new Set(s); n.add(curPid); return n; });
    setCur(passageStart[curPassageIdx + 1] ?? safeCur);
    window.scrollTo(0, 0);
  };
  const goPrevPassage = () => {
    if (curPassageIdx <= 0) return;
    setCur(passageStart[curPassageIdx - 1] ?? 0);
    window.scrollTo(0, 0);
  };

  const retry = async (fresh) => {
    setRetrying(true);
    try {
      const r = await api.post(`/test-section/sessions/${sessionId}/retry`, { fresh }, { silent: true });
      if (fresh) window.dispatchEvent(new Event("pm-billing-refresh"));
      nav(`/app/practice-test/session/${r.data.id}`); setCur(0); setRetryOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.reason || "Couldn't start that session.");
      alert(msg);
    } finally { setRetrying(false); }
  };

  if (!session) return <div className="py-24 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (session.building) return <BuildingView session={session} />;
  if (session.status === "finished") return (
    <>
      <ReviewView session={session} onRetry={() => setRetryOpen(true)} retrying={retrying} />
      <AlertDialog open={retryOpen} onOpenChange={setRetryOpen}>
        <AlertDialogContent data-testid="sp-retry-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Retry how?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>Same passages (free)</b> locks you to the exact questions you just saw, so you can practise them until you own them.<br />
              <b>Brand-new passages</b> starts a fresh session on the same section and mode, excluding every passage you've already seen. Free plans pay the usual 0.5 credit per new passage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row">
            <button onClick={() => setRetryOpen(false)} className="pm-btn-secondary" data-testid="sp-retry-cancel">Cancel</button>
            <button onClick={() => retry(false)} disabled={retrying} className="pm-btn-secondary" data-testid="sp-retry-same">Same passages · free</button>
            <button onClick={() => retry(true)} disabled={retrying} className="pm-btn-primary" data-testid="sp-retry-fresh">Brand-new passages</button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
  if (!items.length) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center" data-testid="sp-empty">
        <CircleAlert className="mx-auto mb-3" size={28} style={{ color: "var(--weakness)" }} />
        <h2 className="font-display text-xl font-bold mb-2" data-testid="sp-empty-title">We couldn&apos;t build your passage</h2>
        <p className="mb-6 text-sm" style={{ color: "var(--text-secondary)" }} data-testid="sp-empty-reason">
          {session.slots.some((s) => s.fail_reason === "ai_busy")
            ? "Our AI writer is at capacity right now. Please try again in a little while."
            : "The passage didn't pass our accuracy checks in time, so we didn't show it to you."} Any credits were refunded.
        </p>
        <div className="flex gap-3 justify-center">
          <Link to="/app/practice-test" className="pm-btn-secondary" data-testid="sp-empty-back">Back to Test Section</Link>
          <button onClick={() => retry(true)} disabled={retrying} className="pm-btn-primary inline-flex items-center gap-2" data-testid="sp-empty-retry">
            {retrying ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />} Try again
          </button>
        </div>
      </div>
    );
  }
  const it = items[Math.min(cur, items.length - 1)];
  const ans = session.answers[it.key] || {};
  const answered = items.filter((x) => session.answers[x.key]?.choice != null).length;

  return (
    <div className="max-w-7xl mx-auto" data-testid="sp-runner">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <Link to="/app/practice-test" className="text-xs inline-flex items-center gap-1 hover:underline" style={{ color: "var(--primary)" }} data-testid="sp-exit">
            <ArrowLeft size={12} /> Test Section
          </Link>
          <div className="font-display text-2xl font-bold" data-testid="sp-mode-name">{session.mode_name}</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {left != null && (
            <div className="pm-badge font-mono text-base inline-flex items-center gap-1.5" style={{ background: left < 120 ? "var(--weakness)" : "var(--text)", color: "var(--paper)" }} data-testid="sp-timer">
              <Timer size={14} /> {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")}
            </div>
          )}
          <button onClick={() => setReviewOpen(true)} className="pm-btn-secondary inline-flex items-center gap-1.5" data-testid="sp-review-screen-btn">
            <ListChecks size={14} /> Review screen
          </button>
          <button onClick={() => setConfirm(true)} className="pm-btn-primary" data-testid="sp-end-btn">End &amp; review</button>
        </div>
      </div>
      <div className="mb-5"><Palette items={items} current={cur} answers={session.answers} onJump={setCur} /></div>
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="pm-card p-6 min-w-0 lg:max-h-[75vh] lg:overflow-y-auto"><PassagePane passage={it.p} highlightKey={`sci-${sessionId}-${it.p.id}`} /></div>
        <div className="pm-card p-6 min-w-0">
          <QuestionPane q={it.q} number={cur + 1} answer={ans} onChoose={(i) => save(it.key, { choice: i })}
            review={isLocked} struck={strikes[it.key] || []} onStrike={isLocked ? undefined : (i) => toggleStrike(it.key, i)} />
          {isLocked && (
            <p className="text-xs mt-3 px-3 py-2 rounded-lg inline-flex items-center gap-1.5" style={{ background: "var(--muted)", color: "var(--text-secondary)" }} data-testid="sp-locked-note">
              <ListChecks size={12} /> You already moved past this passage. Answers are locked for review.
            </p>
          )}
          <div className="flex items-center justify-between mt-6 gap-2 flex-wrap">
            <button onClick={() => setCur(cur - 1)} disabled={cur === 0 || cur === passageStart[curPassageIdx]} className="pm-btn-secondary inline-flex items-center gap-1 disabled:opacity-40" data-testid="sp-prev"><ArrowLeft size={14} /> Prev</button>
            <button onClick={() => save(it.key, { flagged: !ans.flagged })} disabled={isLocked} className="text-sm inline-flex items-center gap-1.5 px-3 py-2 rounded-xl disabled:opacity-40"
              style={{ background: ans.flagged ? "#e08a2a" : "var(--muted)", color: ans.flagged ? "white" : "var(--text-secondary)" }} data-testid="sp-flag">
              <Flag size={14} /> {ans.flagged ? "Flagged" : "Flag for review"}
            </button>
            <button onClick={() => setCur(cur + 1)} disabled={cur >= items.length - 1 || (passageStart[curPassageIdx + 1] != null && cur >= passageStart[curPassageIdx + 1] - 1)} className="pm-btn-secondary inline-flex items-center gap-1 disabled:opacity-40" data-testid="sp-next">Next <ArrowRight size={14} /></button>
          </div>
          {passages.length > 1 && (
            <div className="mt-4 pt-4 border-t flex items-center justify-between gap-2 flex-wrap" style={{ borderColor: "var(--border)" }}>
              <button onClick={goPrevPassage} disabled={curPassageIdx === 0} className="pm-btn-secondary inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="sp-prev-passage">
                <ArrowLeft size={14} /> Back to previous passage
              </button>
              <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }} data-testid="sp-passage-counter">Passage {curPassageIdx + 1} / {passages.length}</span>
              <button onClick={goNextPassage} className="pm-btn-primary inline-flex items-center gap-1.5" data-testid="sp-next-passage">
                {curPassageIdx >= passages.length - 1 ? <>Finish &amp; see summary <ListChecks size={14} /></> : <>Next passage <ArrowRight size={14} /></>}
              </button>
            </div>
          )}
          {passages.length === 1 && !isLocked && (
            <div className="mt-4 pt-4 border-t flex justify-end" style={{ borderColor: "var(--border)" }}>
              <button onClick={() => setConfirm(true)} className="pm-btn-primary inline-flex items-center gap-1.5" data-testid="sp-next-passage">
                Finish &amp; see summary <ListChecks size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
      <ReviewScreen open={reviewOpen} onOpenChange={setReviewOpen} onJump={setCur}
        items={items.map((x, i) => ({ index: i, label: `${x.p.title} — ${x.q.stem}`, answered: session.answers[x.key]?.choice != null, flagged: !!session.answers[x.key]?.flagged }))} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="sp-end-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>End this section?</AlertDialogTitle>
            <AlertDialogDescription>You've answered {answered} of {items.length} questions. Unanswered questions count as wrong.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sp-end-cancel">Keep working</AlertDialogCancel>
            <AlertDialogAction onClick={finish} disabled={finishing} data-testid="sp-end-confirm">{finishing ? "Scoring…" : "End & review"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
